/**
 * DOES A UNIFORM SCALE LEAVE THIS MTEXT'S LAID-OUT BLOCK *SIMILAR* TO ITSELF?
 * The precondition under which the cached measurement (DXF groups 42/43) may be
 * SCALED with the entity instead of thrown away — @codex PR #794 round 16 P2,
 * issue #804.
 *
 * # The question, and why a transform has to ask it
 *
 * Groups 42/43 are NOT inputs. The Autodesk DXF reference marks both "read-only,
 * ignored if supplied" (42: "horizontal width of the characters that make up the
 * mtext entity. This value will always be equal to or less than the value of
 * [group 41]"; 43: "vertical height of the mtext entity") — quoted verbatim in
 * `ezdxf` 1.4.4 beside `rect_width`/`rect_height`
 * (`ezdxf/entities/mtext.py:151-156`). AutoCAD RECOMPUTES them from the laid-out
 * block. A repo that cannot run AutoCAD's font measurement therefore has exactly
 * one honest question to ask on a transform: **is the value AutoCAD would
 * recompute knowable from the value it already wrote?**
 *
 * For a UNIFORM scale by `k` the answer is `old × k`, EXACTLY, **when the whole
 * laid-out block scales by `k`** — the contents, the style and the inline codes
 * are untouched, `nominalHeight` scales by `k`, the wrap column
 * (`referenceWidth`) scales by `k` so every line breaks after the same glyph,
 * and every advance scales by `k`. The new block is then GEOMETRICALLY SIMILAR
 * to the old one and both of its measured extents are `k` times the old ones.
 *
 * # THE CONTENT CLASS THAT BREAKS IT — an absolute inline `\\H`
 *
 * `\\H2x;` is RELATIVE (it multiplies the height in force) and `\\H2;` is
 * ABSOLUTE — it REPLACES the height with a WORLD value.
 * `wcsBoundsText.ts::mtextPaintedLayout` implements exactly that
 * (`isMultiplier ? state.height * value : value / nominalHeight`), so an
 * absolute run paints at `n` world units NO MATTER what `nominalHeight` is, and
 * `scaleEntityRadii` does not rewrite contents. After a ×2 SCALE:
 *
 *   1. that run's advances stay put while every other run's double — the block
 *      is NOT similar, and neither 42 nor 43 scales by `k`;
 *   2. the wrap-column invariance goes with it — the column doubles while that
 *      run's accumulated advance does not, so lines can break after DIFFERENT
 *      glyphs and the line count itself can change.
 *
 * Scaling 42/43 on such content would swap round-15's stale pair for a
 * DIFFERENTLY stale pair. This predicate is what keeps that from happening: the
 * caller scales on `true` and CLEARS on `false` — round 14's action for round
 * 14's stated reason (the new measurement is unknowable here, and 0 is the "no
 * group 42/43" state the writer omits and AutoCAD recomputes on load).
 *
 * # KNOWN INCOMPLETE, and it is a SECOND defect, not this one
 *
 * The fully AutoCAD-faithful answer is to scale the absolute inline `\\H` WITH
 * the entity, after which similarity holds for it too. That is what `ezdxf` does
 * — `MText.transform()` calls `tools/text.py::scale_mtext_inline_commands`
 * whenever `char_height` changes, and that helper scales ONLY the absolute
 * `\\H<value>;` form, skipping the relative `x` one. Today a SCALE in this repo
 * leaves an absolute-height run RENDERING at its old size, which is wrong
 * independently of groups 42/43.
 *
 * It is deliberately NOT done here (CLAUDE.md ПРИОРИТЕТ #1's one allowed
 * exception — a known gap with a completion plan, filed as #805, which also
 * carries the plan below so it is not re-derived):
 *
 *   * drive the rewrite off THIS repo's tokenisation — the flush-on-style-code,
 *     `;`-terminated, `\\\\`-escape-aware walk `wcsBoundsText.ts` documents — and
 *     NOT off `ezdxf`'s `content.split(r"\\H")`, which cannot tell an escaped
 *     literal `\\\\H` from the code;
 *   * do NOT inherit `ezdxf`'s `f"{new_size:.3g}"` — three significant digits is
 *     a fidelity loss with no reason behind it; emit a round-trip-exact number;
 *   * a contents rewrite is not self-inverse through a string, so SCALE's
 *     `computeInverse` needs a pre-edit contents snapshot, the same shape as its
 *     existing `restoreAttributeHeights` / `restoreAttributeRotations`;
 *   * THREE call sites, not one, and they must share the rewrite rather than
 *     grow three copies of it (ПРИОРИТЕТ #2.1): `command-engine::
 *     scaleEntityRadii`'s MText arm, and BOTH EXPLODE twins
 *     (`command-engine::blockMutations.ts` and
 *     `apps/web::blockReducerHelpers.ts`), which scale a block child's
 *     `nominalHeight` by the INSERT's scale and pass `contents` through
 *     verbatim — so an exploded MTEXT has the identical absolute-`\\H`
 *     problem. (Those two also use one `Math.abs(scaleX)` for the
 *     WIDTH-like `referenceWidth` and the HEIGHT-like `nominalHeight` alike,
 *     a separate pre-existing bug of the "one factor for a directional pair"
 *     shape — #806, and `scaleEntityRadii.ts`'s sweep paragraph has it.)
 *
 * `ezdxf` is NOT a reference in favour of leaving 42/43 alone, either: its own
 * `MText.transform()` scales 40, 41, the inline codes, the insertion, the
 * direction and the column data and leaves `rect_width`/`rect_height` untouched,
 * while `disassemble.py` then reads that stale pair back as the entity's box —
 * the same gap #804 reports, and its `MTextColumns.transform` (which DOES scale
 * `total_width`/`total_height`/`defined_height`/`heights`) shows that scaling a
 * cached measured extent with the transform is ezdxf's own position elsewhere.
 */

import { mtextPaintedLayout, type MtextPaintedLayout } from "./wcsBoundsText";

/**
 * Relative agreement required of each compared quantity between any two
 * probes.
 *
 * The walks run the SAME per-run arithmetic against different heights,
 * so a genuinely similar block agrees to a few ULPs and never exactly — the
 * tolerance absorbs FLOATING-POINT EVALUATION ERROR IN THE WALK, and nothing
 * else. That is the whole of its job, and it is why the probes are separated
 * by a CONSTANT rather than by the caller's factor (@codex PR #794 round 18
 * P2): the smallest breach this predicate can see is ONE painted
 * absolute-`\\H` run, whose world height does not move AT ALL between the
 * probes, so at a separation of {@link SIMILARITY_PROBE_FACTOR} its own
 * comparison misses by exactly `1 − 1/2 = 0.5` — eight and a half orders of
 * magnitude above this tolerance, for EVERY caller, with no dependence on how
 * far the caller's factor happens to sit from 1.
 *
 * THAT CLAIM IS ONLY TRUE INSIDE A GUARDED DOMAIN, which is what round 20
 * added: a product that underflows to a SUBNORMAL or to zero, or overflows to
 * `Infinity`, is not off by ULPs — it is off by everything — and no tolerance
 * can be sized for it. Every quantity compared here is checked NORMAL first
 * (see THE DOMAIN IS GUARDED), so what is left for this number to absorb
 * really is the walk's own re-association and nothing else.
 *
 * WHAT THAT REPLACES. While the probes were separated by the CALLER's
 * `absFactor` the same miss was `|1 − k| / max(k, 1)`, which goes to ZERO as
 * `k` approaches 1: at `k = 1.0000000005` an absolute `\\H5;` run misses by
 * `2.5e-9` against an expected `5.0000000025`, a RELATIVE miss of `5e-10`, so
 * it slipped under this 1e-9 and read as proportional. The verdict was then a
 * property of the FACTOR as much as of the CONTENT — a tolerance absorbing
 * signal instead of evaluation error. No value of this constant could have
 * fixed that, because `|1 − k| / max(k, 1)` is continuous in `k` and passes
 * through every tolerance on its way to 0.
 */
const SIMILARITY_RELATIVE_TOLERANCE = 1e-9;

/**
 * The factor the DEGREE probe is separated by — FIXED, and deliberately NOT
 * the caller's `absFactor` (@codex PR #794 round 18 P2). It is not the only
 * height the walk is read at any more: round 20 added a third probe at the
 * caller's own destination, because this constant answers what DEGREE a run
 * is and cannot answer how the arithmetic CONDITIONS at a height it never
 * visits (see AT THE CALLER'S OWN HEIGHT). The two are complementary and
 * both are required; neither replaces the other.
 *
 * WHY A CONSTANT IS THE RIGHT INPUT. The question this predicate answers is
 * "does every painted run's world height ride the nominal height, or is one
 * of them a WORLD constant?", and that is a property of `contents` ALONE.
 * `wcsBoundsText.ts` stores an absolute `\\H5;` as `state.runHeight = 5 /
 * nominalHeight`, so that run's `runHeightWorld` is homogeneous of DEGREE 0
 * in the nominal height while every other run's is DEGREE 1. The height chain
 * is purely multiplicative — a relative `\\H2x;` multiplies whatever is in
 * force, an absolute one replaces it — so a run is either `C × h` or `C × v`
 * and there is no third degree and no mixture. A degree-0 run is therefore
 * invisible at exactly ONE separation, `k = 1`, which never reaches a probe;
 * at `× 2` it misses proportionality by a full half.
 *
 * WHY CERTIFYING AT 2 LICENSES SCALING BY THE CALLER'S `k`. The certificate is
 * STRONGER than a per-factor one, not weaker: proportional content is
 * proportional at EVERY positive factor. Every quantity the walk derives from
 * a run is that run's `runHeightWorld` times a coefficient with no dependence
 * on the nominal height, and every `Math.max` in the path is positively
 * homogeneous over terms that are themselves proportional — so no branch of
 * the walk can be taken differently at `2h` than at `kh` (checked branch by
 * branch under NO FACTOR-DEPENDENT BRANCH below, and swept empirically: 9 380
 * (contents, height, factor) combinations over `|k − 1| > 1e-6`, factors
 * 1e-8…1e8 and nominal heights 1e-6…1e12, produced not one verdict that
 * depended on the factor).
 *
 * WHAT ELSE IT BUYS. The walk is never evaluated at `nominalHeight × 1e300`,
 * so the probe can no longer manufacture an overflow or an underflow of its
 * own — a shear product `tan × aboveWorld` at an extreme `k` goes to ±∞ and
 * refused a block AutoCAD lays out perfectly well. Those were verdicts about
 * the DOUBLE, not about the drawing.
 *
 * 2 rather than 3 or 1.5 for one reason: a power of two is exact in binary, so
 * `nominalHeight × 2` introduces no rounding of its own and the only float
 * error left in the comparison is the walk's own re-association — exactly what
 * {@link SIMILARITY_RELATIVE_TOLERANCE} is sized for.
 */
const SIMILARITY_PROBE_FACTOR = 2;

/** ONE walk of `contents` at `nominalHeight`, read at ALL THREE of the levels
 *  the verdict needs. */
interface LayoutProbe {
  /**
   * Every PAINTED run's world height AS THE PARSER COMPUTES IT — the walk's
   * mirror of `mtextParser.ts`'s own `heightWorld` chain, in the parser's
   * order and with its `> 0 ? … : nominal` fallback (@codex PR #794 round 20
   * P2). THE VERDICT IS MADE OF THESE. It is the RUN level, the only channel
   * in which a run that dominates no extremum is visible, and it is the
   * renderer's own arithmetic rather than a reconstruction of it.
   */
  readonly parserHeights: readonly number[];
  /**
   * The SAME runs' heights in the WALK's own factor model (`state.runHeight ×
   * nominalHeight`) — what every aggregate below, and every bound this module
   * takes, is built out of. It is NOT compared: wherever both models are well
   * conditioned it IS {@link parserHeights}, and where they part it is the
   * parser that paints. It is read so THE DOMAIN IS GUARDED can see it.
   */
  readonly runHeights: readonly number[];
  /** What those runs roll up into — the walk's own aggregates, compared as a
   *  backstop for degeneracies that live outside the run heights. */
  readonly layout: MtextPaintedLayout;
}

/**
 * Walk `contents` once and report both levels.
 *
 * Groups 42/43 are not involved and cannot be: this reads the WALK, not the
 * cached pair the caller is deciding about. `mtextBoxSize` — round 16's probe
 * — is deliberately no longer it: that function is a strictly WEAKER reading
 * of this same walk (it returns group 42 verbatim as `width`, clamps
 * `selectPaintedRight` to it, and folds the walk into four global maxima),
 * and those maxima are exactly where round 17's dominated run hid.
 */
function probeLayout(
  contents: string,
  nominalHeight: number,
  lineSpacingFactor: number,
): LayoutProbe {
  const runHeights: number[] = [];
  const parserHeights: number[] = [];
  const layout = mtextPaintedLayout(
    contents,
    nominalHeight,
    lineSpacingFactor,
    (heightWorld, parserHeightWorld) => {
      runHeights.push(heightWorld);
      parserHeights.push(parserHeightWorld);
    },
  );
  return { parserHeights, runHeights, layout };
}

/**
 * The smallest NORMAL double, `2^-1022` ≈ 2.225e-308. At and above it every
 * double carries the full 53-bit significand; below it (a SUBNORMAL, down to
 * `Number.MIN_VALUE`) the significand is truncated toward zero and a product
 * loses digits it can never get back — see THE DOMAIN IS GUARDED.
 */
const MIN_NORMAL_DOUBLE = 2 ** -1022;

/** A quantity the comparison may be trusted with: finite, positive, NORMAL.
 *  Every height in this module is positive by construction (the parser's
 *  fallback and `scaleState`'s clamp both replace a non-positive one), so
 *  this reads as a magnitude test and not as a sign test. */
function wellConditioned(x: number): boolean {
  return Number.isFinite(x) && x >= MIN_NORMAL_DOUBLE;
}

/**
 * THE ORDINARY-MAGNITUDE BAND the two NOMINAL heights must both lie in —
 * @codex PR #794 round 21, 4057969957. `[1e-6, 1e6]`, INCLUSIVE at both ends.
 *
 * WHAT IT IS FOR: making the probe DOMAIN symmetric under the forward/inverse
 * swap. See THE DOMAIN IS SYMMETRIC in the function's own doc — the pair
 * `{h, h × k}` this is applied to is, as a SET, the same pair the undo's call
 * is applied to.
 *
 * WHY THESE TWO NUMBERS. Neither the DXF reference nor this repo bounds an
 * MTEXT's nominal height — group 40 is `z.number().positive()` in
 * `mtextEntitySchema` and "text height" in Autodesk's own table, with no
 * range — so the band is not derived, it is CHOSEN. It spans TWELVE orders
 * of magnitude, against the four or so real text heights occupy: an ISO
 * 2.5 mm annotation, a 1:1000 site plan's 3 m label, an imperial 0.125 in
 * note all sit inside 1e-4…1e4 whatever unit the file declares, so each
 * bound is about two orders of magnitude past anything a drawing reaches,
 * and the band is four hundred times wider than the range it is for. (Two
 * orders is the honest number, not the twelve the span might suggest — the
 * span is the interval, the headroom is the distance to its edge.)
 *
 * It is applied to the DESTINATION as well as the source, so what it says is
 * "the text was an ordinary size before the scale and is an ordinary size
 * after it" — a scale that takes a 2.5-unit label to 2.5e6 units has left
 * the class of drawings this certificate is for, and the answer there is the
 * same `false` every other unjudgeable case gets: the pair is cleared and
 * AutoCAD recomputes it, which is the state of every MTEXT that never
 * carried 42/43.
 *
 * WHAT IT BUYS BESIDES SYMMETRY: with both nominals ≤ 1e6 the constant
 * probe's own height, `2 × max(h, h × k)`, is at most 2e6, so it CANNOT
 * overflow. Round 20's finding B (an MTEXT at 1e308 refused because `2h` is
 * `Infinity`) and round 21's F1 (the same overflow appearing only on the UNDO
 * leg) both stop being reachable rather than being handled.
 *
 * WHAT IT DOES NOT REPLACE, and all three are load-bearing inside the band —
 * each is pinned by a test that shows it is the ONLY guard that refuses its
 * own witness:
 *
 *   * {@link wellConditioned} on the REPORTED heights. The band bounds the
 *     probe SCAFFOLDING; the normality guard bounds the CONTENT's own chain.
 *     At the in-band nominal 1, `\\H1e-310x;A` reports a SUBNORMAL 1e-310 at
 *     `h` and 2e-310 at `2h` — perfectly proportional, both nominals in band,
 *     and only the normality guard refuses it.
 *   * the DESTINATION probe (round 20). Inside the band `k` still ranges over
 *     1e-12…1e12, so a chain that is well conditioned at `h` need not be at
 *     `h × k`: `\\H5e-324x;\\H1e308x;A` at `h = 1000`, `k = 1e-6` reports
 *     4.940656458412465e-13 at `h` and exactly twice that at `2h`, every
 *     reading NORMAL — while at the destination 1e-3 the first product
 *     underflows to zero, the parser falls back to the nominal, and the run
 *     paints 1e-3 where 4.94e-19 was owed. Only the destination probe sees
 *     that.
 *   * the CONSTANT `2h` probe (round 18). It is what refuses the near-unity
 *     band, which lives at the most ordinary magnitudes there are.
 */
const ORDINARY_NOMINAL_MIN = 1e-6;
const ORDINARY_NOMINAL_MAX = 1e6;

/** Is this nominal height an ORDINARY drawing magnitude — see the band's own
 *  doc? Inclusive at both ends. */
function inOrdinaryNominalBand(h: number): boolean {
  return h >= ORDINARY_NOMINAL_MIN && h <= ORDINARY_NOMINAL_MAX;
}

/** Every per-run quantity ONE probe contributes, inside the domain. Both
 *  channels are checked, not just the compared one: the aggregates are built
 *  from {@link LayoutProbe.runHeights}, so a degenerate reading there makes
 *  the backstop meaningless even when the parser's own heights are clean. */
function probeIsWellConditioned(p: LayoutProbe): boolean {
  return (
    p.parserHeights.every(wellConditioned) &&
    p.runHeights.every(wellConditioned)
  );
}

/**
 * `b ≈ a × k`, relative to the larger magnitude so an exact 0 on both sides
 * passes and a 0-vs-non-0 pair fails.
 *
 * AN OVERFLOWED TARGET IS NOT A TOLERANCE QUESTION — @codex PR #794 round 21,
 * 4057969961. Both operands are screened finite, but the PRODUCT `a × k` need
 * not be: at `a = 1e308, k = 2` it is `Infinity`, `scale` is then `Infinity`
 * too, `Math.abs(b − Infinity)` is `Infinity` for every finite `b`, and
 * `Infinity <= Infinity × 1e-9` is `true`. So the comparison accepted ANY
 * finite `b` as proportional to an overflowed expectation —
 * `proportional(1e308, 42, 2)` answered `true`. `expected` is therefore
 * screened before the tolerance is applied, and the refusal is
 * UNCONDITIONAL rather than left to the caller's domain: this is the
 * module's one comparison primitive and the hole is a hole for every caller,
 * including a future one whose inputs the magnitude band below does not
 * bound. (Under the band the product cannot overflow from THIS module's own
 * call sites any more — which is why the fix needs its own witness rather
 * than an end-to-end one; see `mtextScaleSimilarity.test.ts`.)
 *
 * The screen is on the PRODUCT, not on `k`: a non-finite `k` reaches this
 * through `expected` and is refused by the same line, and a `k` that
 * UNDERFLOWS the product to 0 is not a lost verdict — 0 against a finite `b`
 * is exactly the 0-vs-non-0 pair the `scale` form already fails.
 */
export function proportional(a: number, b: number, k: number): boolean {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  const expected = a * k;
  if (!Number.isFinite(expected)) return false;
  const scale = Math.max(Math.abs(expected), Math.abs(b));
  if (scale === 0) return true;
  return Math.abs(b - expected) <= scale * SIMILARITY_RELATIVE_TOLERANCE;
}

/** `b ≈ a` — the DEGREE-0 comparison, for the walk's dimensionless rollups
 *  (`lineAdvanceFactor` and `inkFactor` are multiples OF the nominal height,
 *  not world lengths), which a similar block leaves UNCHANGED rather than
 *  scaling. Reading one of them at the wrong degree would refuse every plain
 *  block and accept exactly the absolute-`\\H` one this predicate exists to
 *  refuse, so the degree is spelled out at every call site. */
function unchanged(a: number, b: number): boolean {
  return proportional(a, b, 1);
}

/**
 * The walk's AGGREGATES, compared at their OWN degree in the nominal height
 * — the BACKSTOP the run pass is layered on, and not a restatement of it:
 * these are what catch a degeneracy living OUTSIDE the run heights, such as
 * a `\\Q1e308;` tangent that poisons all four shear extremes with ±∞ while
 * every run height stays finite and proportional.
 *
 * Degree 1 for the world-unit interval ends and the shear, degree 0 for
 * `lineAdvanceFactor` and `inkFactor`, which are MULTIPLES of the nominal
 * height and not world lengths. Reading one at the wrong degree would refuse
 * every plain block and accept exactly the absolute-`\\H` one this predicate
 * exists to refuse, so the degree is spelled out at every call.
 */
function aggregatesProportional(
  b: MtextPaintedLayout,
  a: MtextPaintedLayout,
  k: number,
): boolean {
  if (b.lineCount !== a.lineCount || b.lineGlyphs !== a.lineGlyphs) {
    return false;
  }
  if (!unchanged(b.lineAdvanceFactor, a.lineAdvanceFactor)) return false;
  if (!unchanged(b.inkFactor, a.inkFactor)) return false;
  return (
    proportional(b.paintedAbove, a.paintedAbove, k) &&
    proportional(b.paintedBelow, a.paintedBelow, k) &&
    proportional(b.shearAboveMin, a.shearAboveMin, k) &&
    proportional(b.shearAboveMax, a.shearAboveMax, k) &&
    proportional(b.shearBelowMin, a.shearBelowMin, k) &&
    proportional(b.shearBelowMax, a.shearBelowMax, k)
  );
}

/**
 * The MTEXT fields the verdict depends on — the ONE input shape both callers
 * pass, so the forward clear (`command-engine::scaleEntityRadii`, holding a
 * typed `MtextEntity`) and the undo snapshot
 * (`command-engine::geometryMutations`' `computeInverse`, holding a
 * loosely-typed document row) cannot disagree about which entities are
 * similar.
 *
 * EVERY field is optional here and normalised INSIDE the function, against
 * `mtextEntitySchema` and nothing else, because a caller-side `?? 0` that
 * happens not to match a schema default is precisely how the two sites would
 * drift: the forward would clear an entity the inverse did not snapshot and
 * the measurement would be gone for good. The rules, field by field:
 *
 *   * `contents` and `nominalHeight` are REQUIRED by the schema (`z.string()`,
 *     `z.number().positive()`) and have NO default, so a row missing either is
 *     not a judgeable MTEXT — verdict `false`, the sound direction, never a
 *     substituted `""` / `0` that would silently answer `true`.
 *   * `lineSpacingFactor` is `z.number().default(1)` — 1.
 *   * `columnType` is `.default(0)`, `columnWidth` and `columnGutter` are
 *     `.default(0)`, `columnHeights` is `.default([])`.
 *
 * `referenceWidth` (group 41) is NOT a field here, and its absence is
 * load-bearing rather than an omission — see THE WRAP COLUMN IS A
 * PRECONDITION below.
 */
export interface MtextLayoutShapeInput {
  readonly contents?: string;
  readonly nominalHeight?: number;
  readonly lineSpacingFactor?: number;
  readonly columnType?: number;
  readonly columnWidth?: number;
  readonly columnGutter?: number;
  readonly columnHeights?: ReadonlyArray<number>;
}

/**
 * Would a UNIFORM scale by `absFactor` (a magnitude — the caller has already
 * taken `Math.abs`) leave this MTEXT's laid-out block SIMILAR to itself, so
 * that AutoCAD's recomputed groups 42/43 would be the carried pair times
 * `absFactor`?
 *
 * `false` is the SOUND answer for everything it cannot establish, because the
 * two ways of being wrong are not symmetric: a false `false` costs the entity
 * an exact measurement it could have kept (it falls back on the contents walk,
 * i.e. the state of every MTEXT that never carried 42/43), while a false
 * `true` writes a NEW wrong measurement into the model — the very defect #804
 * reports.
 *
 * It answers by MEASURING rather than by re-parsing: the same
 * {@link mtextPaintedLayout} walk is run at THREE heights — `nominalHeight`,
 * `nominalHeight × ` {@link SIMILARITY_PROBE_FACTOR} (a FIXED separation and
 * NOT the caller's factor, see THE PROBE FACTOR IS A CONSTANT below) and
 * `nominalHeight × absFactor`, the height the entity will actually have (see
 * AT THE CALLER'S OWN HEIGHT) — and the comparison is made AT RUN LEVEL,
 * every painted run having to come out `× 2` at the constant and `× k` at
 * the destination, with the walk's aggregates compared on top as a backstop.
 * That keeps the grammar in ONE place (a
 * second `\\H` scanner here would be a copy of `mtextPaintedLayout`'s
 * tokenisation to keep in step by hand — the mistake rounds 14 and 15
 * already paid for), and it is strictly more accurate than a syntactic test:
 * `\\H5;` followed by NO glyphs emits no run, is reported by nothing and
 * correctly leaves the block similar, which a "contents contain an absolute
 * `\\H`" scanner would get wrong.
 *
 * WHAT IS MEASURED IS THE PARSER'S OWN NUMBER. The walk carries a run height
 * twice over — as a dimensionless FACTOR of the nominal height, which is what
 * its own geometry and every aggregate below are built from, and as the
 * PARSER's `heightWorld`, accumulated in the parser's order with no division
 * anywhere. The verdict is made of the second, because that is what the
 * renderer paints; the first is read only so the domain guard can see it.
 * Rounds 19 and 20 were both the CONVERSION between the two, never either
 * chain (see AT THE CALLER'S OWN HEIGHT).
 *
 * # WHY RUN LEVEL, AND NOT THE FOUR PAINTED REACHES IT USED TO READ
 *
 * @codex PR #794 round 17 P2. Until round 17 the probe compared four GLOBAL
 * EXTREMA of the walk — `paintedRight` / `paintedLeft` / `paintedAbove` /
 * `paintedBelow` out of `mtextBoxSize`. Every one of them is a MAXIMUM over
 * runs or over lines, so a painted absolute-height run that is neither the
 * widest nor the tallest reaches NONE of them and the probe could not see it
 * at either height.
 *
 * WITNESS, measured and pinned in `mtextScaleSimilarity.test.ts`:
 * `"hello world this is a very long relative line\\P{\\H0.5;tiny}"` at the
 * 2.5 nominal. All four reaches come out at EXACTLY ×2 under a ×2 scale (the
 * long relative paragraph dominates the width, the line grid dominates both
 * vertical ends), the old probe therefore returned `true`, and SCALE doubled
 * groups 42/43 — although the `\\H0.5;` paragraph still paints at 0.5 world
 * units afterwards and AutoCAD's recomputed pair is NOT `old × 2`.
 *
 * The review reached that defect through WRAPPING — a wrapped
 * absolute-height paragraph can UNWRAP once a scale widens the column, so
 * the real line count and height change — and the hole is WIDER than the
 * route to it: no wrapping is required, only domination. Wrapping is what
 * turns an invisible run into a visibly wrong one.
 *
 * RUN LEVEL IS NECESSARY AND SUFFICIENT, wrapping included, which is why it
 * REPLACES the extrema instead of joining them:
 *
 *   * SUFFICIENT — the emit sequence (which runs, in which order, with how
 *     many glyphs, under which `\\W` / `\\T` / `\\Q` / `\\A`) is a function of
 *     `contents` ALONE, and `contents` is what SCALE does not rewrite (#805).
 *     Every per-run world quantity the layout is built out of is that run's
 *     `runHeightWorld` times a factor with NO dependence on the nominal
 *     height (`wcsBoundsText.ts`: `advance = runHeight × width × tracking`,
 *     `ink = runHeight × max(1, width)`, `offsetWorld = align ×
 *     runHeightWorld`, the two `tan` products, and `dropWorld`, which is
 *     `mtextLineAdvance` — itself a multiple of the nominal height). So if
 *     every run's `runHeightWorld` scales by `k`, every glyph advance scales
 *     by `k`; the caller scales the wrap column by the same `k`, so each line
 *     breaks after the SAME glyph, the line COUNT is unchanged, and the whole
 *     laid-out block — AutoCAD's, not merely the walk's — is similar by `k`.
 *     The probe establishes that hypothesis at the FIXED separation of 2 and
 *     it transfers to the caller's `k` unchanged, because a run is either
 *     degree 1 or degree 0 in the nominal height and no factor but 1 fails to
 *     separate those two ({@link SIMILARITY_PROBE_FACTOR}).
 *   * NECESSARY — a run whose world height does NOT scale paints at a size
 *     the rest of the block has left behind, so the block is not similar, no
 *     matter how invisible that run is to every maximum.
 *
 * THE AGGREGATES ARE STILL COMPARED, as a BACKSTOP and not as a restatement
 * of the pass above: they are what catches a degeneracy living OUTSIDE the
 * run heights. A `\\Q1e308;` tangent overflows and poisons all four shear
 * extremes with ±∞ at BOTH heights while every run height stays finite and
 * proportional; the dimensionless rollups (`lineAdvanceFactor`, `inkFactor`,
 * `lineGlyphs`, `lineCount`) pin the composition of the lines those runs land
 * on. Each is compared at its OWN degree in the nominal height — degree 1 for
 * the world-unit interval ends and the shear, degree 0 for the factors —
 * because reading one at the wrong degree would accept exactly the block this
 * refuses. Comparing them can only turn a `true` into a `false`, the sound
 * direction.
 *
 * # THE PROBE FACTOR IS A CONSTANT, NOT THE CALLER'S
 *
 * @codex PR #794 round 18 P2. Until round 18 BOTH probes ran at the caller's
 * `absFactor`, which made the verdict a property of that factor as much as of
 * the entity.
 *
 * WITNESS, measured and pinned in `mtextScaleSimilarity.test.ts`:
 * `"A{\\H5;BBBBBBBB}"` at the 2.5 nominal under `absFactor = 1.0000000005`.
 * The `\\H5;` run paints 5 world units at BOTH probe heights, so it misses an
 * expected `5 × 1.0000000005 = 5.0000000025` by `2.5e-9` — a RELATIVE miss of
 * `5e-10`, under {@link SIMILARITY_RELATIVE_TOLERANCE}. The run read as
 * proportional, the predicate answered `true`, and SCALE carried groups 42/43
 * through a transform whose recomputed block contains an UNCHANGED run. The
 * band is a RANGE and not a point (`|k − 1| ≲ 1e-9`, on both sides of 1), and
 * because the WHOLE probe ran at that factor a near-unity `k` defeated the
 * predicate for every content class at once rather than for one run.
 *
 * The absolute error such a false `true` writes is small — bounded by the
 * absolute run's share of the extent times `|k − 1| < 1e-9` relative, i.e.
 * 1e-4 units on a block 1e5 units across, not a visible one. What had to go
 * is the SHAPE of the mistake: a tolerance may cover the arithmetic of the
 * measurement and never the quantity being measured.
 *
 * `absFactor` therefore never enters the DEGREE question, which is what
 * round 18 was about: the constant probe alone decides whether a run rides
 * the nominal height, and it decides it identically for every caller. Where
 * `absFactor` does enter — the `=== 1` early-out, the finite-and-positive
 * validation of the caller's own argument, and round 20's DESTINATION probe
 * — it is never a separation the degree test is read at; the destination
 * asks a different question (does the chain still CONDITION there?) whose
 * answer genuinely depends on the height, and can only refuse.
 *
 * NO FACTOR-DEPENDENT BRANCH IN THE DEGREE TEST, CHECKED RATHER THAN ASSUMED
 * — the fixed probe would be unsound if the walk could make content
 * proportional at 2 and not at some other `k` FOR A REASON THE DESTINATION
 * PROBE DOES NOT SEE. The three candidates, all content-driven:
 *
 *   * the DEGENERATE-HEIGHT CLAMPS. `mtextPaintedLayout`'s absolute-`\\H` arm
 *     branches on `nominalHeight > 0` and `scaleState`'s `runHeight` on
 *     `height > 0`. Both probes run at a POSITIVE nominal height (a
 *     non-positive one is refused before probing) and `\\H` / `\\W` / `\\T`
 *     admit only `value > 0`, so both branches go the same way at every
 *     positive height — with ONE exception, which round 19 found and round 20
 *     widened: a height chain can clamp at one height and not at another. No
 *     choice of probe factor repairs it, and no guard read at the probes
 *     detects it, so the chain is now the PARSER's and it is read AT THE
 *     CALLER'S OWN HEIGHT as well as at the constant.
 *   * the `\\Q` TANGENT OVERFLOW. `state.tan` is `Math.tan` of the CODE's own
 *     argument and no nominal height enters it, so `Number.isFinite(state.tan)`
 *     is decided by `contents`. What IS height-dependent is the PRODUCT
 *     `tan × aboveWorld`, which can overflow at an extreme `k` — and a fixed
 *     probe at 2 never reaches that, which is a reason FOR the constant and
 *     not against it.
 *   * the NON-FINITE GUARDS (`maxReach`'s `!Number.isFinite(reach)`, and
 *     {@link proportional}'s own) fire only on a non-finite reach, i.e. on
 *     that same overflow.
 *
 * The remaining `Math.max`es — `Math.max(0, offsetWorld − dropWorld)` and its
 * mirror, `Math.max(1, widthFactor)`, `maxReach`, and
 * `paintedVerticalInterval`'s two grid floors — take a difference or a
 * maximum of terms that are ALL degree 1 in the nominal height once every run
 * is proportional, so each is `h × (const)` and its sign, hence the branch it
 * takes, is the same for every `h > 0`. Where such a branch COULD flip with
 * the height, one of its terms has to be degree 0 — which is an
 * absolute-height run, which the RUN pass has already refused.
 *
 * # AT THE CALLER'S OWN HEIGHT, IN THE PARSER'S OWN CHAIN
 *
 * @codex PR #794 round 20 P2, findings A, B and C — three findings in one
 * round, and ONE root cause behind rounds 19 and 20 alike. The walk models a
 * run height as a DIMENSIONLESS FACTOR of the nominal one; the parser models
 * it in WORLD UNITS. Everything that has gone wrong since round 18 lives in
 * the CONVERSION between those two — a divide by the nominal here, a multiply
 * back there — which loses everything, not a few ULPs, wherever an
 * intermediate is subnormal, zero or infinite. Round 19 patched one corner of
 * it with a reported flag; round 20 produced three more corners. The
 * conversion is therefore GONE from the reported path:
 *
 *   * `InlineScaleState` now carries the PARSER's `heightWorld` beside its own
 *     factor — the same accumulation `mtextParser.ts` performs, in the same
 *     order (`state.heightWorld * val` / a bare `val` / the entity's nominal
 *     to begin with), with the same `> 0` fallback at the point of emit. No
 *     quotient, so nothing to underflow;
 *   * a painted ABSOLUTE `\\H5;` reports a flat 5 at every height, which the
 *     CONSTANT probe sees miss proportionality by a half, for any caller.
 *     That is what the round-19 FLAG was for, and it is why the flag is gone:
 *     the world chain says what the flag said, as a measurement, and says it
 *     at the extremes where the quotient could not. STICKY and `{`/`}`-scoped
 *     come free, being state — a multiple of a world value IS a world value;
 *   * the walk is read at a THIRD height, `nominalHeight × absFactor`. This is
 *     NOT a return to round 17's caller-factor separation — the constant probe
 *     stays, and round 18's near-unity band stays refused by it; the
 *     destination is an ADDITIONAL refusal, and an AND can only make a `true`
 *     into a `false`.
 *
 * WHY THE DESTINATION PROBE IS UNAVOIDABLE, AND WHY NO GUARD REPLACES IT.
 * Finding C: `nominalHeight = 1`, contents `\\H1e-200x;\\H1e-124x;A`,
 * `absFactor = 1e100`. The FACTOR chain is height-INDEPENDENT and underflows
 * to 0 at every probe, so `scaleState`'s clamp made both probes report a run
 * riding the nominal and the predicate answered `true`. The parser's WORLD
 * chain underflows at `h = 1` (and falls back to the nominal) but NOT at
 * `1e100`, where it is `9.999999999999999e-225`: after the scale that run
 * stops following the nominal, the block is not similar, and 42/43 were
 * scaled anyway.
 *
 * A DOMAIN GUARD AT THE TWO PROBES DOES NOT CLOSE THAT CLASS, which was
 * checked rather than assumed before this shape was built. `nominalHeight =
 * 1`, contents `\\H1e-300x;\\H1e300x;\\H2x;A`, `absFactor = 1e-100`: every
 * intermediate of BOTH chains is NORMAL at `h = 1` and at `2h = 2`, both
 * reported heights are 2 and 4, and the run is perfectly proportional there
 * — while at the destination `1e-100` the parser's `1e-100 × 1e-300`
 * underflows to 0, the rest of the chain multiplies 0, and the fallback
 * paints the run at the nominal `1e-100` where similarity wants `2e-100`.
 * Probing in the parser's order does not close it either, for the same
 * reason: the conditioning of a height chain is a property OF THE HEIGHT IT
 * IS EVALUATED AT, and `h` and `2h` do not determine it at `k × h`. The only
 * sound place to ask what happens at the caller's height is the caller's
 * height.
 *
 * # THE DOMAIN IS GUARDED
 *
 * @codex PR #794 round 20 P2, findings A and B. Every quantity the verdict
 * rests on must be FINITE and NORMAL ({@link MIN_NORMAL_DOUBLE}) at every
 * probe: the probe nominal heights, and every reported run height in BOTH
 * channels. Anything else — a subnormal, a zero, an infinity — is refused
 * without comparison.
 *
 * WHY. {@link SIMILARITY_RELATIVE_TOLERANCE} claims to absorb EVALUATION
 * error and nothing else. That claim is only true where the arithmetic is
 * well conditioned; a product that has fallen into the subnormal range has
 * lost significand digits, and one that has overflowed has lost everything.
 * Guarding the domain makes the claim TRUE rather than hopeful, and it closes
 * a whole corner by construction instead of one witness at a time.
 *
 * WHAT IT COSTS, and it is accepted deliberately. Finding B: an MTEXT at
 * `nominalHeight = 1e308` with `\\W1;A` is entirely relative and WOULD be
 * similar under `× 0.5`, but the constant probe's `2h` is `Infinity`, so the
 * probe cannot be taken and the pair is CLEARED. That is an over-refusal, in
 * the sound direction, for a drawing that does not exist — and it is a
 * DELIBERATE DOMAIN REFUSAL rather than a measurement, pinned as one in
 * `mtextScaleSimilarity.test.ts` so it is not later "fixed" into an
 * acceptance. Every alternative (a probe that flips direction on overflow, a
 * tolerance widened at the extremes) reopens the float-extremes surface that
 * has now produced four findings in two rounds. (Round 21's BAND now refuses
 * that entity one step EARLIER, on its nominal height rather than on the
 * probe's overflow — the verdict is unchanged and so is the test; what
 * changed is that the overflow is no longer REACHED. See THE DOMAIN IS
 * SYMMETRIC.)
 *
 * WHAT IT DOES NOT DO is refuse finding A. `nominalHeight = 2.5` with
 * `\\H1e-200;\\H1e-200x;A` underflows the FACTOR chain to 0, but the parser's
 * world chain underflows too, at EVERY height, so the parser falls back to
 * the nominal at every height and the run really does ride it. Round 19's
 * flag refused that block because absoluteness was reported as a BIT that
 * survived the underflow; the world chain reports a NUMBER, the number rides
 * the nominal, and the block is ACCEPTED — which is what the review asked
 * for. The measurement is right there; nothing needs to be excused.
 *
 * THE OTHER DIRECTION OF THE OLD CONVERSION is covered by the same guard: a
 * huge absolute height against a tiny nominal (`\\H1e130;` at `nominalHeight
 * = 1e-200`) sends the walk's own factor, and with it its reported height, to
 * `Infinity` — outside the domain, refused before any comparison, and refused
 * again by the parser channel, which reports a flat 1e130 at both probes.
 *
 * # THE DOMAIN IS SYMMETRIC — the ORDINARY-MAGNITUDE BAND
 *
 * @codex PR #794 round 21, 4057969957. A guard that refuses on one leg of a
 * SCALE and not on the other does not merely over-refuse: it LOSES DATA.
 *
 * WITNESS, measured and pinned in `mtextScaleSimilarity.test.ts`: an MTEXT
 * measuring `\\W1;A` — entirely relative — at `nominalHeight = 1e307`, under
 * `× 10`. The forward's three probe heights are `1e307`, `2e307` and `1e308`,
 * all finite and normal, the block is similar, and the predicate answers
 * `true`. `computeInverse` asks the SAME question about the SAME row and
 * therefore also gets `true`, so it snapshots NOTHING. The undo then runs
 * `scaleEntityRadii` over the SCALED entity — nominal `1e308` — at `× 0.1`,
 * where the constant probe's `2h` is `Infinity`: the domain guard refuses,
 * the caller CLEARS the pair, and there is no `restoreMtextMeasurements`
 * entry to put it back. Undo restores the geometry and destroys the
 * measurement, permanently. (This was reported to the reviewer in round 20 as
 * safe on the grounds that "the cleared case is snapshotted by
 * `computeInverse`". That reassurance was wrong for exactly the reason the
 * finding gives: `computeInverse` snapshots what the FORWARD clears, and the
 * forward here clears nothing.)
 *
 * THE FIX IS THE DOMAIN, NOT A SNAPSHOT RULE. The review offered both —
 * "either snapshot whenever the inverse-direction verdict can differ, or make
 * the probe domain symmetric under the forward/inverse swap". The BAND takes
 * the second: the forward is judged on the nominal pair `{h, h × k}` and the
 * undo on `{h × k, h}`, THE SAME SET, so a domain refusal is now a property
 * of the pair rather than of which end of it the caller happens to be
 * standing at. The overflow that produced the witness is not handled, it is
 * unreachable: `2 × max(h, h × k) ≤ 2e6`.
 *
 * A SECOND ASYMMETRY, of the same shape, sat on the path the band does NOT
 * gate — found by this round's own forward/inverse sweep rather than
 * reported. The no-backslash early-out answered `true` at EVERY magnitude,
 * including one whose scaled nominal is not representable: `"A"` at
 * `nominalHeight = 1e308` under `× 2` was carried, the caller wrote an
 * `Infinity` height and an `Infinity` measurement, and the undo — reading a
 * row whose height is not finite — refused and cleared. The destination
 * height is therefore validated FINITE AND POSITIVE in front of every
 * early-out, which makes that path symmetric too and narrows nothing else:
 * a representable destination keeps its byte-faithful `true`.
 *
 * WHAT THE BAND STILL CANNOT MAKE EXACT, stated rather than papered over. The
 * undo's pair is `{h × k, (h × k) × (1 / k)}`, and the second element is `h`
 * only up to a round trip: at `h = 1e6` (the upper bound itself) and
 * `k = 1/15` the scaled nominal is 66666.66666666667, in band, and multiplying
 * it back by `15` gives 1000000.0000000001, OUT of band. A hard threshold
 * cannot be exactly stable under a float round trip — any bound has an edge,
 * and moving the edge only moves which heights sit on it. That residual is
 * closed on the OTHER side, where it can be closed exactly:
 * `command-engine::geometryMutations`' `computeInverse` snapshots when EITHER
 * direction's verdict clears, so "undo never loses a measurement" holds by
 * construction and does not rest on this band being swap-exact.
 *
 * # THE WRAP COLUMN IS A PRECONDITION, NOT AN INPUT
 *
 * Group 41 (`referenceWidth`) is deliberately absent from
 * {@link MtextLayoutShapeInput}. Until round 17 it was accepted here and
 * passed to `mtextBoxSize` under a comment claiming the two probes had to
 * differ in the column so a change in where lines BREAK could not read as a
 * break of similarity — and `mtextBoxSize` reads NONE of it. In
 * `wcsBoundsText.ts`'s own words, the wrap column "plays NO part in the
 * width". The argument was passed, ignored, and validated nothing while
 * reading as though it did.
 *
 * Reading it would not have helped either, because this walk does not wrap at
 * all — it counts PARAGRAPHS, not wrapped lines (`mtextBoxSize`'s U4). What
 * makes wrapping safe here is not a measurement but the SUFFICIENCY argument
 * above: a UNIFORM scale by `k` multiplies the column by `k` as well as the
 * nominal height, and once every run scales by `k` too, the break points are
 * identical BY SIMILARITY — same column, same advances, same glyph at every
 * break. The column is therefore a PRECONDITION THE CALLER OWES, and the one
 * caller pays it in the same object literal that scales the height
 * (`command-engine::scaleEntityRadii`: `referenceWidth: entity.referenceWidth
 * * absFactor`). A transform that scaled the height WITHOUT the column would
 * be asking a question this predicate does not answer.
 *
 * WHY A KNOWN-INACCURATE WALK MAY CERTIFY AN EXACT VALUE. The obvious
 * objection: this repo documents the very same walk as an over-reporting
 * BOUND that disagrees with AutoCAD by a wide margin — on the round-13
 * witness it reaches 112.785 where AutoCAD's own column ends at 50 and the
 * bundled atlases paint 26-31 (`wcsBoundsText.ts` header F1). How does a
 * 2.3×-wrong estimator get to certify that AutoCAD's number scales exactly?
 *
 * Because the predicate never reads the probe's ABSOLUTE value. It reads only
 * RATIOS of probes of the SAME walk over the SAME contents at different
 * heights — `h` against `2h`, and `h` against `k × h`. Every systematic error
 * the walk carries — `MAX_GLYPH_ADVANCE`'s
 * worst-case 1.22 against a real ≈0.3, `MAX_GLYPH_INK`, the left bearing, the
 * descent constant — is a per-glyph COEFFICIENT, not a per-entity offset, so
 * it multiplies both probes identically and divides out of the ratio.
 *
 * VERIFIED TERM BY TERM against `wcsBoundsText.ts`, because an ADDITIVE term
 * — one in world units rather than a multiple of the run height — would NOT
 * cancel, and that would be a real hole rather than a documentation gap:
 *
 *   * the X reach: `mtextPaintedBound` is `nominalHeight × (advanceFactor ×
 *     MAX_GLYPH_ADVANCE + inkFactor × MAX_GLYPH_INK)`, and the left bearing is
 *     `inkFactor × MAX_GLYPH_LEFT_BEARING × nominalHeight`. `inkFactor` is
 *     `runHeight × Math.max(1, widthFactor)` — a DIMENSIONLESS factor times a
 *     run height, so still degree 1.
 *   * the Y interval (`mtextPaintedLayout`'s per-run block): `runHeightWorld =
 *     state.runHeight × nominalHeight`, `offsetWorld = state.align ×
 *     runHeightWorld`, `dropWorld = (lineCount − 1) × lineAdvanceWorld` (and
 *     `mtextLineAdvance` is itself a multiple of `nominalHeight`), with
 *     `aboveWorld` / `belowWorld` sums of those under a `Math.max(0, …)` of a
 *     DIFFERENCE of two of them.
 *   * the `\\Q` shear is a height-independent `tan` times one of those ends.
 *   * every `Math.max` in the path — those guards, `maxReach`, and
 *     `Math.max(1, widthFactor)` — is POSITIVELY HOMOGENEOUS over terms that
 *     are themselves proportional, so the branch it takes CANNOT change under
 *     a positive uniform scale. A max of proportional terms is proportional.
 *
 * (`estimateGlyphRunWidth`'s per-line `Math.max(height, …)` floor is NOT in
 * this path — it is the TEXT / ATTDEF heuristic, and MTEXT stopped coming
 * through it in round 8 P2.)
 *
 * So the ratio tests PROPORTIONALITY of the laid-out block in the walk's own
 * model — and proportionality is a property AutoCAD's real measurement shares,
 * since both are sums of per-glyph advances times a run height. A poor
 * estimator of SIZE is a faithful detector of SHAPE, and SHAPE is the only
 * question asked. Where the two could still part company the failure lands on
 * `false`, which costs an exact measurement rather than inventing a wrong one.
 *
 * THE ONE NON-PROPORTIONAL INPUT IS THE ONE THIS EXISTS TO CATCH, and the walk
 * already models it correctly. An absolute `\\H5;` is stored as a FACTOR by
 * dividing by the nominal, so `state.runHeight = 5 / nominalHeight` and
 * `runHeightWorld = (5 / nominalHeight) × nominalHeight = 5` — a CONSTANT that
 * does not move when the nominal does, which is exactly what the DXF semantics
 * say an absolute inline height is. That is why measuring beats scanning here:
 * the detector is not looking for a `\\H` TOKEN, it is reading the walk's own
 * arithmetic, which already encodes the scale-invariance. (The `(5 / h) × h`
 * round trip lands within one ULP of 5 — some seven orders of magnitude below
 * {@link SIMILARITY_RELATIVE_TOLERANCE} — so it cannot produce a false
 * `false` either.) THAT HOLDS ONLY WHILE THE QUOTIENT IS NORMAL: rounds 19
 * and 20 found the extremes where it is not, and the verdict is therefore no
 * longer read off that quotient at all — the walk carries the PARSER's own
 * world chain beside its factor and the comparison is made on THAT, inside a
 * guarded domain (see AT THE CALLER'S OWN HEIGHT and THE DOMAIN IS GUARDED).
 *
 * FORWARD CONSTRAINT, not a hedge: this rests on the walk staying homogeneous
 * of degree 1 in `nominalHeight`, which is an invariant the module already
 * maintains deliberately — `mtextEstimatedHeightInterval`'s own doc keeps the
 * DECORATION thickness floor (`max(heightWorld × 0.05, 1)`, WORLD units, U2 /
 * #796) OUT of the interval precisely because folding a non-homogeneous term
 * in "would break the annotative law round 7 pinned", the same law
 * `wcsBoundsLeaf.ts::mtextLocalCorners` re-evaluates a per-scale context
 * under. If #796 is ever implemented by folding that absolute floor INTO the
 * walk, it becomes the first additive term, the cancellation above stops
 * holding, and this predicate must be revisited with it.
 *
 * COLUMNS are checked separately and syntactically, because they are the case
 * the probe CANNOT see: the walk does not read `columnWidth` /
 * `columnGutter` / `columnHeights` at all, and `scaleEntityRadii` does not
 * scale them either — so a columned MTEXT's real block keeps its old column
 * geometry while its text grows, AutoCAD's recomputation would not be
 * `old × k`, and the pair must be cleared. (That SCALE leaves the column
 * lengths themselves behind is a separate, pre-existing defect of its own —
 * #807; this predicate only makes sure #804's fix stays sound in its presence.)
 *
 * COST. The probe is THREE contents walks since round 20 (the source, the
 * constant separation and the caller's destination), and the three early-outs
 * in front of it are what keep a SCALE over a large selection from paying for
 * them:
 *
 *   1. the CALLER skips this function entirely for an MTEXT carrying no
 *      measurement (`42 === 0 && 43 === 0`) — the state of every MTEXT this
 *      editor created and of every import whose file omitted the groups, i.e.
 *      the overwhelming majority;
 *   2. `absFactor === 1` is answered without a walk;
 *   2a. an out-of-band nominal (round 21's BAND) is refused before the first
 *      walk, so the extremes cost nothing either;
 *   3. contents with NO BACKSLASH cannot carry an inline code at all, so the
 *      block is similar BY INSPECTION — the same reasoning
 *      `mtextPaintedLayout`'s own no-backslash fast path rests on, and it
 *      covers the overwhelming majority of the MTEXTs that DO carry a
 *      measurement.
 *
 * What is left paying three walks is an MTEXT that carries AutoCAD's
 * measurement AND formatting codes. RE-MEASURED for round 20 (node 22 under
 * `tsx`, a 48-character MTEXT with an inline `\\H` run, 10 000 calls after a
 * 2 000-call warm-up): 75 ms through all three walks, 0.3 ms through the
 * no-backslash early-out and 0.2 ms through the `× 1` one — against 50 ms on
 * the same bench for round 19's two, i.e. the third walk costs what the other
 * two do, as it must. The second reported channel is free: one extra `push`
 * per PAINTED run, and nothing at all on the bounds path, which passes no
 * callback. So the worst realistic SCALE — ten thousand formatted, measured
 * MTEXTs selected at once — costs ≈ 75 ms in the forward pass and the same
 * again in `computeInverse`, against a document walk that already dominates
 * it; the ordinary case does not reach the walks at all.
 */
export function mtextUniformScalePreservesLayoutShape(
  e: MtextLayoutShapeInput,
  absFactor: number,
): boolean {
  // A no-op scale is trivially similarity-preserving, and answering it here
  // keeps the caller's `× 1` byte-faithful without a probe.
  if (absFactor === 1) return true;
  if (!Number.isFinite(absFactor) || absFactor <= 0) return false;
  const { contents, nominalHeight } = e;
  // The two schema-REQUIRED fields. A row without them is not judgeable, and
  // substituting a default the schema does not have would answer `true` for an
  // entity nobody measured.
  if (typeof contents !== "string") return false;
  if (
    typeof nominalHeight !== "number" ||
    !Number.isFinite(nominalHeight) ||
    nominalHeight <= 0
  ) {
    // A degenerate height is not judgeable either: the walk clamps it to 0,
    // every reach collapses to 0 and every probe agrees vacuously.
    return false;
  }
  const scaledNominal = nominalHeight * absFactor;
  // THE DESTINATION HEIGHT MUST BE A HEIGHT — checked HERE, in front of
  // every early-out that follows, and not beside the probes
  // (@codex PR #794 round 21, 4057969957, found by this round's own
  // forward/inverse sweep rather than reported).
  //
  // `nominalHeight × absFactor` is what `scaleEntityRadii` writes into the
  // scaled entity. When it OVERFLOWS (`1e308 × 2`) or UNDERFLOWS to zero (a
  // subnormal nominal times a small factor) the scaled entity has no
  // judgeable height at all — and the undo leg, reading that entity, refuses
  // on exactly this test. The no-backslash early-out below did not look, so
  // it answered `true` for `"A"` at `nominalHeight = 1e308` under `× 2`, the
  // caller carried 42/43 into an entity whose height is `Infinity`, and the
  // undo then cleared a pair `computeInverse` had not snapshotted — the F1
  // defect exactly, on a content class the ORDINARY-MAGNITUDE BAND below
  // deliberately does not reach. Refusing here makes the early-out symmetric
  // too: the undo's own destination is `(h × k) × (1 / k)`, which is finite
  // and positive whenever `h` is.
  if (!Number.isFinite(scaledNominal) || scaledNominal <= 0) return false;
  // Absolute column lengths the transform leaves behind — invisible to the
  // walk, so tested against the schema defaults rather than measured.
  if (
    (e.columnType ?? 0) !== 0 ||
    (e.columnWidth ?? 0) !== 0 ||
    (e.columnGutter ?? 0) !== 0 ||
    (e.columnHeights?.length ?? 0) !== 0
  ) {
    return false;
  }
  // Early-out 3: no backslash ⇒ no inline code ⇒ every advance is a multiple
  // of the nominal height ⇒ similar, without walking anything.
  if (!contents.includes("\\")) return true;
  const lineSpacingFactor = e.lineSpacingFactor ?? 1;
  // THE THREE HEIGHTS THE WALK IS READ AT. The ONLY input that moves between
  // them is the nominal height: everything else comes out of `contents`,
  // which SCALE does not rewrite (#805), and the wrap column is not read at
  // all (see THE WRAP COLUMN above).
  //   * the SOURCE height — the block as it stands;
  //   * the CONSTANT separation, never `absFactor` (see THE PROBE FACTOR IS A
  //     CONSTANT): a run that pins a WORLD height is invisible at `k ≈ 1`;
  //   * the DESTINATION, `nominalHeight × absFactor` — the SAME expression
  //     `scaleEntityRadii` writes into the scaled entity, because the height
  //     chain's CONDITIONING is a property of the height it is evaluated at
  //     and no pair of probes elsewhere determines it (see AT THE CALLER'S
  //     OWN HEIGHT, @codex PR #794 round 20 P2).
  const probeNominal = nominalHeight * SIMILARITY_PROBE_FACTOR;
  // THE DOMAIN IS SYMMETRIC — the ORDINARY-MAGNITUDE BAND, on BOTH nominals
  // (@codex PR #794 round 21, 4057969957). The pair `{h, h × k}` is the SAME
  // SET the undo leg's own call is judged on, so a verdict taken here cannot
  // be contradicted by a DOMAIN refusal there — which is how the forward
  // could answer `true`, leave `computeInverse` with nothing to snapshot, and
  // then have the undo clear a pair it could no longer restore. See THE
  // DOMAIN IS SYMMETRIC in this function's doc.
  if (
    !inOrdinaryNominalBand(nominalHeight) ||
    !inOrdinaryNominalBand(scaledNominal)
  ) {
    return false;
  }
  // THE DOMAIN IS GUARDED — the nominal ends of it, before any walk runs.
  // Subsumed by the band for these two (2e6 is finite and 1e-6 is normal),
  // and kept because the band is a POLICY about drawings while this is the
  // arithmetic precondition {@link SIMILARITY_RELATIVE_TOLERANCE} is sized
  // against: the run-level guard below is the same predicate, and the two
  // must not drift apart.
  if (!wellConditioned(probeNominal) || !wellConditioned(scaledNominal)) {
    return false;
  }
  const before = probeLayout(contents, nominalHeight, lineSpacingFactor);
  const after = probeLayout(contents, probeNominal, lineSpacingFactor);
  const scaled = probeLayout(contents, scaledNominal, lineSpacingFactor);
  // …and the run ends of it. Outside the normal band the walk's arithmetic
  // has lost digits the tolerance below does not model, so the verdict is not
  // a measurement of the drawing but of the double — `false`, which costs an
  // exact measurement no real drawing wanted (see THE DOMAIN IS GUARDED).
  if (
    !probeIsWellConditioned(before) ||
    !probeIsWellConditioned(after) ||
    !probeIsWellConditioned(scaled)
  ) {
    return false;
  }
  // RUN LEVEL — necessary AND sufficient, and the only level at which a
  // DOMINATED run is visible at all. The emit sequence is a function of
  // `contents` alone, so a length mismatch is unreachable rather than
  // expected; it is compared because `false` is the sound answer to anything
  // this cannot establish, a future walk that made the sequence
  // height-dependent included.
  if (
    before.parserHeights.length !== after.parserHeights.length ||
    before.parserHeights.length !== scaled.parserHeights.length
  ) {
    return false;
  }
  for (let i = 0; i < before.parserHeights.length; i += 1) {
    const source = before.parserHeights[i]!;
    // The DEGREE test: does this run ride the nominal height, or pin a world
    // one? Read at the constant, so the answer cannot depend on how far the
    // caller's factor sits from 1 (round 18).
    if (
      !proportional(source, after.parserHeights[i]!, SIMILARITY_PROBE_FACTOR)
    ) {
      return false;
    }
    // The CONDITIONING test: the same run, at the height the entity will
    // actually have. Riding the nominal at `h` and at `2h` does NOT imply
    // riding it at `k × h` — the chain can pass through the subnormal range
    // in between, and then the parser's fallback replaces the whole product
    // (round 20 P2, findings A and C).
    if (!proportional(source, scaled.parserHeights[i]!, absFactor)) {
      return false;
    }
  }
  // AGGREGATE BACKSTOP, at BOTH separations — the same two the run level is
  // read at, for the same two reasons.
  return (
    aggregatesProportional(
      before.layout,
      after.layout,
      SIMILARITY_PROBE_FACTOR,
    ) && aggregatesProportional(before.layout, scaled.layout, absFactor)
  );
}
